import { useCallback, useEffect, useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { s, authHeaders } from '../listPageStyles';
import { fmtMoney, fmtDate } from './campaignUtils';

function pick(o, ...keys) { if (!o) return undefined; for (const k of keys) if (o[k] != null) return o[k]; return undefined; }

function KpiCard({ icon, label, value, sub, accent }) {
  return (
    <div className="cmp-kpi" style={{ position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div className="l">{label}</div>
        <span aria-hidden="true" style={{ fontSize: 16, opacity: 0.85, color: accent }}>{icon}</span>
      </div>
      <div className="v">{value}</div>
      {sub != null && <div className="s">{sub}</div>}
    </div>
  );
}

export function CampaignStatsTab({ campaignId, form }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!campaignId) return;
    setLoading(true);
    fetch(`/api/campaigns/${campaignId}/statistics`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => setData((p && (p.Data || p.data)) || p))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  if (!campaignId) {
    return <div className="cmp-empty">Kampanya kaydedildikten sonra istatistikler burada görünür.</div>;
  }

  const totalUsage = pick(data, 'totalUsage') ?? 0;
  const consumedBudget = pick(data, 'consumedBudget') ?? 0;
  const giftCount = pick(data, 'giftCount') ?? 0;
  const remaining = pick(data, 'remaining');
  const lastUsedDate = pick(data, 'lastUsedDate');
  const maxApplications = pick(data, 'maxApplications') ?? Number(form.maxApplications) ?? 0;
  const budgetLimit = pick(data, 'budgetLimit') ?? Number(form.budgetLimit) ?? 0;

  const rawDaily = pick(data, 'daily') ?? [];
  const daily = (rawDaily.length ? rawDaily : Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (29 - i));
    return { date: d.toISOString(), count: 0, amount: 0 };
  })).map((d) => ({
    date: fmtDate(d.date).slice(0, 5),
    count: d.count,
    amount: Number(d.amount || 0),
  }));
  const topCustomers = pick(data, 'topCustomers') ?? [];
  const topDistributors = pick(data, 'topDistributors') ?? [];

  const usagePct = maxApplications > 0 ? Math.min(100, Math.round((totalUsage / maxApplications) * 100)) : 0;
  const budgetPct = budgetLimit > 0 ? Math.min(100, Math.round((consumedBudget / budgetLimit) * 100)) : 0;
  const hasUsage = totalUsage > 0;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <h3 className="cmp-section-title">📈 Kullanım İstatistikleri</h3>
          <p className="cmp-section-sub">Promosyonun kullanım ve performans verileri.</p>
        </div>
        <button style={s.btnSecondary} onClick={load} disabled={loading}>{loading ? '...' : '↻ Yenile'}</button>
      </div>

      <div className="cmp-kpi-grid">
        <KpiCard icon="📈" accent="#2563eb" label="Toplam Kullanım" value={totalUsage}
          sub={lastUsedDate ? `Son: ${fmtDate(lastUsedDate)}` : (maxApplications > 0 ? `/ ${maxApplications} limit` : 'limitsiz')} />
        <KpiCard icon="💲" accent="#15803d" label="Tüketilen Bütçe" value={`₺${fmtMoney(consumedBudget)}`}
          sub={budgetLimit > 0 ? `%${budgetPct} kullanıldı` : 'bütçe yok'} />
        <KpiCard icon="🎁" accent="#d97706" label="Hediye Ürünler" value={giftCount} sub="dağıtılan adet" />
        <KpiCard icon="⚠" accent="#7c3aed" label="Limit Durumu" value={remaining != null ? remaining : '∞'}
          sub={remaining != null ? '✓ Kullanılabilir' : 'limitsiz'} />
      </div>

      {(maxApplications > 0 || budgetLimit > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 16 }}>
          {maxApplications > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}><span>Kullanım Limiti</span><span>{totalUsage} / {maxApplications}</span></div>
              <div className="cmp-progress"><span style={{ width: `${usagePct}%` }} /></div>
            </div>
          )}
          {budgetLimit > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}><span>Bütçe Durumu</span><span>₺{fmtMoney(consumedBudget)} / ₺{fmtMoney(budgetLimit)}</span></div>
              <div className="cmp-progress"><span style={{ width: `${budgetPct}%`, background: '#15803d' }} /></div>
            </div>
          )}
        </div>
      )}

      <h4 className="cmp-section-title" style={{ fontSize: 15, marginTop: 20 }}>Günlük Kullanım Geçmişi (Son 30 Gün)</h4>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={daily} margin={{ top: 10, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="rgba(15,23,42,0.06)" vertical={false} />
          <XAxis dataKey="date" fontSize={10} tickLine={false} interval={4} />
          <YAxis yAxisId="left" fontSize={10} tickLine={false} allowDecimals={false} label={{ value: 'Kullanım Sayısı', angle: -90, position: 'insideLeft', fontSize: 10, fill: '#94a3b8' }} />
          <YAxis yAxisId="right" orientation="right" fontSize={10} tickLine={false} label={{ value: 'Bütçe (TL)', angle: 90, position: 'insideRight', fontSize: 10, fill: '#94a3b8' }} />
          <Tooltip formatter={(v, name) => [name === 'Bütçe (TL)' ? `₺${fmtMoney(v)}` : v, name]} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="right" dataKey="amount" name="Bütçe (TL)" fill="rgba(226,68,92,0.35)" radius={[4, 4, 0, 0]} />
          <Line yAxisId="left" dataKey="count" name="Kullanım Sayısı" stroke="#2563eb" strokeWidth={2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
      {!hasUsage && (
        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-secondary,#6b7280)', marginTop: 6 }}>
          Henüz kullanım verisi yok — saha siparişlerinde bu kampanya uygulandıkça grafik dolacak.
        </p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 20 }}>
        <div>
          <h4 className="cmp-section-title" style={{ fontSize: 14 }}>👥 En Çok Kullanan Müşteriler</h4>
          {topCustomers.length ? (
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary,#6b7280)' }}><th>Müşteri</th><th style={{ textAlign: 'right' }}>Kullanım</th><th style={{ textAlign: 'right' }}>Bütçe</th></tr></thead>
              <tbody>{topCustomers.map((c, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border-color,#eee)' }}>
                  <td>{c.customerName || c.customerId}<div style={{ fontSize: 10, color: '#9ca3af' }}>{c.customerCode}</div></td>
                  <td style={{ textAlign: 'right' }}>{c.count}</td>
                  <td style={{ textAlign: 'right' }}>₺{fmtMoney(c.amount)}</td>
                </tr>
              ))}</tbody>
            </table>
          ) : <div className="cmp-empty" style={{ padding: 16 }}>Veri yok</div>}
        </div>
        <div>
          <h4 className="cmp-section-title" style={{ fontSize: 14 }}>🏢 Distribütör Kullanımları</h4>
          {topDistributors.length ? (
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary,#6b7280)' }}><th>Distribütör</th><th style={{ textAlign: 'right' }}>Kullanım</th><th style={{ textAlign: 'right' }}>Bütçe</th></tr></thead>
              <tbody>{topDistributors.map((d, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border-color,#eee)' }}>
                  <td>{d.distributorName || d.distributorId}</td>
                  <td style={{ textAlign: 'right' }}>{d.count}</td>
                  <td style={{ textAlign: 'right' }}>₺{fmtMoney(d.amount)}</td>
                </tr>
              ))}</tbody>
            </table>
          ) : <div className="cmp-empty" style={{ padding: 16 }}>Veri yok</div>}
        </div>
      </div>
    </div>
  );
}

export default CampaignStatsTab;
